import { AuthContextProvider } from 'src/contexts/AuthContext';
import { Layout as BaseLayout, message } from 'antd';
import { Suspense, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import Sidebar from 'src/components/admin/sidebar';
import { getStoredRole } from 'src/hooks/useAuthValidation';
import { ROUTES } from 'src/types/routes';

export default function AdminDefaultLayout() {
  // Decoded synchronously so the gate decisions below run on the first render,
  // before any protected child outlet can mount and fire admin-only requests.
  //
  // SECURITY (TCX-797): `role` comes from the unverified atob-decoded JWT (see
  // getStoredRole). The redirects below are a UX gate ONLY — they keep the
  // wrong role out of the admin shell UI, but they are NOT the access-control
  // boundary. Every admin endpoint MUST enforce @Roles(ADMIN) server-side; a
  // client can forge the role and still hit the API directly (see SEC-06/SEC-10).
  const role = getStoredRole();
  const { pathname } = useLocation();
  const errorShownRef = useRef(false);

  // TCX-666: super-admins (JWT role 'superAdmin') are admitted to the admin
  // shell so they can reach the Administrators screen.
  if (role && role !== 'admin' && role !== 'superAdmin') {
    if (!errorShownRef.current) {
      message.error('You do not have permission to access this page.');
      errorShownRef.current = true;
    }
    return <Navigate to={ROUTES.ADMIN_DASHBOARD_SIGN_IN} />;
  }

  // TCX-666: super-admins are scoped to the Administrators screen — every other
  // admin screen is @Roles(ADMIN)-only on the BE and would 403. Redirect any
  // deep-link / bookmark to a non-admin-management route back to Administrators
  // (centralizes the scope so it holds for direct navigation, not just the
  // sidebar/sign-in happy path).
  if (role === 'superAdmin' && !pathname.startsWith(ROUTES.ADMIN_ADMINISTRATORS)) {
    return <Navigate to={ROUTES.ADMIN_ADMINISTRATORS} replace />;
  }

  return (
    <AuthContextProvider>
      <BaseLayout>
        <Sidebar />
        <BaseLayout style={{ padding: '48px' }}>
          <Suspense>
            <Outlet />
          </Suspense>
        </BaseLayout>
      </BaseLayout>
    </AuthContextProvider>
  );
}
